import { pick } from 'lodash';
import {
  update,
  contractor,
  contractorW9,
  getContractorPage,
} from '@/api/contractor';
import * as mutations from './mutations';
import * as actions from './actions';

/**
 * Initial state for contractor
 * @returns {{
 *  websiteUrl: null,
 *  phone: null,
 *  companyName: null,
 *  companyAddress: null,
 *  logo: null,
 *  id: null,
 *  email: null,
 *  needConfirmServiceAgreement: null,
 *  needQuiz: null,
 *  hasPage: null,
 *  contractorPages: [],
 *  selectedPage: {},
 *  businessType: null,
 *  specialization: null,
 *  managerCSO: null,
 *  }}
 */
const initialState = () => ({
  id: null,
  email: null,
  companyName: null,
  companyAddress: null,
  websiteUrl: null,
  logo: null,
  phone: null,
  needConfirmServiceAgreement: null,
  needQuiz: null,
  hasPage: null,
  contractorPages: [],
  selectedPage: {},
  plan: { opportunities: [], tier: null },
  ccAccept: null,
  paymentStatus: null,
  managerCSO: null,
  date: null,
  businessType: null,
  businessSpecialization: null,
  typeId: null,
  w9: { status: null, date: null },
});

export default {
  namespaced: true,
  state: () => initialState(),
  getters: {
    /**
     * Get contractor pages
     * @param state
     * @returns []
     */
    pages: (state) => state.contractorPages,
    selectedPage: (state) => state.selectedPage,
    plan: (state) => state.plan,
    paymentStatus: (state) => state.paymentStatus,
    managerCSO: (state) => state.managerCSO,
    contractorId: (state) => state.id,
    isFDA: (state) => !!(state.plan && Array.isArray(state.plan.opportunities) && state.plan.opportunities.includes('FDA')),
    isHELOC: (state) => !!(state.plan && Array.isArray(state.plan.opportunities) && state.plan.opportunities.includes('HELOC')),
    tier: (state) => (state.plan ? state.plan.tier : ''),
    w9: (state) => state.w9,
    w9Status: (state) => state.w9.status,
  },
  mutations: {
    /**
     * Set contractor from payload
     * @param state
     * @param payload
     */
    [mutations.CONTRACTOR_LOAD](state, payload) {
      Object.keys(state).forEach((key) => {
        if (key !== 'contractorPages' && key !== 'w9') {
          state[key] = payload[key];
        }
      });
      state.needConfirmServiceAgreement = !!+payload.needConfirmServiceAgreement;
    },

    /**
     * Update contractor fields from payload
     * @param state
     * @param payload
     */
    [mutations.UPDATE](state, payload) {
      Object.keys(payload).forEach((key) => {
        state[key] = payload[key];
      });
    },

    /**
     * Clear contractor state
     * @param state
     */
    [mutations.CLEAR](state) {
      const init = initialState();
      Object.keys(state).forEach((key) => {
        state[key] = init[key];
      });
    },
    [mutations.SET_CONTRACTOR_PAGES](state, contractorPages) {
      state.contractorPages = contractorPages.sort((a, b) => (a.pageName > b.pageName ? 1 : -1));
      if (contractorPages.length > 0) {
        const foundPage = contractorPages.find((item) => item.id === state.selectedPage?.id);
        state.selectedPage = foundPage || { ...contractorPages[0] };
      } else {
        state.selectedPage = {};
      }
    },
    [mutations.SET_SELECTED_PAGE](state, selectedPage = null) {
      if (selectedPage) {
        state.selectedPage = selectedPage;
      } else if (state.pages && state.pages.length > 0) {
        state.selectedPage = { ...state.pages[0] };
      } else {
        state.selectedPage = {};
      }
    },
    [mutations.SET_W9](state, payload) {
      state.w9 = {
        status: payload && payload.status != null ? payload.status : null,
        date: payload && payload.date != null ? payload.date : null,
      };
    },
  },

  actions: {
    /**
     * Load contractor by id
     * @param context
     * @param contractorId
     * @returns {Promise<boolean>}
     */
    async [actions.CONTRACTOR](context, contractorId) {
      if (!contractorId) {
        context.commit(mutations.CLEAR);
        return true;
      }
      const response = await contractor(contractorId);
      if (response.data) {
        context.commit(mutations.CONTRACTOR_LOAD, response.data);
      } else {
        context.commit(mutations.CLEAR);
      }
      return true;
    },

    /**
     * Update contractor on server
     * @param context
     * @param payload
     * @returns {Promise<void>}
     */
    async [actions.UPDATE_CONTRACTOR](context, payload) {
      const response = await update(payload);

      let keys = Object.keys(payload);
      if (payload instanceof FormData) {
        keys = [];
        payload.forEach((value, key) => keys.push(key));
      }
      const data = pick(response.data, keys);
      if ('managerCSO' in response.data) {
        data.managerCSO = response.data.managerCSO;
      }
      context.commit(mutations.UPDATE, data);
    },

    /**
     * Clear contractor state
     * @param context
     * @returns {Promise<void>}
     */
    async [actions.CLEAR_CONTRACTOR](context) {
      context.commit(mutations.CLEAR);
    },

    async [actions.LOAD_PAGES](context, payload) {
      const response = await getContractorPage(payload);
      context.commit(mutations.SET_CONTRACTOR_PAGES, response.data.data);
      return true;
    },

    [actions.SET_SELECTED_PAGE](context, payload) {
      context.commit(mutations.SET_SELECTED_PAGE, payload);
    },

    /**
     * Load contractor's W-9 status into the store
     * @param context
     * @returns {Promise<void>}
     */
    async [actions.LOAD_W9](context) {
      if (!context.state.id) {
        context.commit(mutations.SET_W9, {});
        return;
      }
      const response = await contractorW9(context.state.id);
      context.commit(mutations.SET_W9, response.data || {});
    },
  },

};
